import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Cake, PartyPopper, CalendarDays, Send, Trophy, Plus, CalendarHeart, Settings } from "lucide-react";
import importantDatesService from "../services/importantDatesService";
import AuthContext from "../context/AuthContext";
import { getAvatarStyle } from "../utils/avatarColors";

const getUserInitials = (name) => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

const formatDate = (dateString) => {
  const date = new Date(`${dateString}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date - today) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const KIND_META = {
  birthday: { Icon: Cake, label: "Birthday", color: "#ec4899" },
  anniversary: { Icon: PartyPopper, label: "Work anniversary", color: "#0ea5e9" },
  event: { Icon: CalendarDays, label: "Event", color: "#8b5cf6" },
};

const ImportantDateRow = ({ item, isAdmin, currentUserId, onAction }) => {
  const meta = KIND_META[item.kind] || KIND_META.event;
  const { Icon } = meta;

  const isPerson = item.kind === "birthday" || item.kind === "anniversary";
  const isSelf = isPerson && item.user?.id === currentUserId;

  let hint = null;
  let HintIcon = null;
  let clickable = false;
  if (isPerson) {
    if (!isSelf) {
      hint = "Send";
      HintIcon = Send;
      clickable = true;
    }
  } else if (!item.use_nominations) {
    // Recognition mode is the default for events; only an explicit true opts
    // an event into the nominations flow.
    hint = "Send";
    HintIcon = Send;
    clickable = true;
  } else if (item.campaign_id && item.campaign_active) {
    hint = "Nominate";
    HintIcon = Trophy;
    clickable = true;
  } else if (item.campaign_id && isAdmin) {
    hint = "Finish campaign setup";
    HintIcon = Settings;
    clickable = true;
  } else if (item.campaign_id) {
    hint = "Nominations open soon";
  } else if (isAdmin) {
    hint = "Create nomination campaign";
    HintIcon = Plus;
    clickable = true;
  } else {
    hint = "Admin has not created a nomination campaign yet";
  }

  const title = isPerson
    ? `${item.user?.full_name || item.user?.email || "Someone"}`
    : item.event?.name || "Event";

  const subtitle = (() => {
    if (item.kind === "anniversary") {
      return `Work anniversary · ${item.years} year${item.years === 1 ? "" : "s"} · ${formatDate(item.date)}`;
    }
    if (item.kind === "birthday") return `Birthday · ${formatDate(item.date)}`;
    return formatDate(item.date);
  })();

  const dimClass = hint
    ? "transition-all duration-200 ease-out group-hover:opacity-25 group-hover:scale-[0.98]"
    : "";

  return (
    <button
      type="button"
      onClick={() => clickable && onAction(item, { isAdmin })}
      disabled={!clickable}
      className={`group relative w-full flex items-center gap-3 p-2.5 rounded-2xl text-left transition-colors duration-200 ${
        clickable ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
      }`}
    >
      {isPerson && item.user ? (
        item.user.avatar_url ? (
          <img
            src={item.user.avatar_url}
            alt={title}
            className={`w-9 h-9 rounded-full object-cover flex-shrink-0 ${dimClass}`}
            style={{ border: `2px solid ${meta.color}` }}
          />
        ) : (
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${dimClass}`}
            style={{
              ...getAvatarStyle(item.user.full_name || item.user.email || item.user.id),
              border: `2px solid ${meta.color}`,
            }}
          >
            {getUserInitials(title)}
          </div>
        )
      ) : (
        <div
          className={`w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 ${dimClass}`}
          style={{ background: `${meta.color}1a` }}
        >
          <Icon style={{ color: meta.color, width: 18, height: 18 }} />
        </div>
      )}

      <div className={`min-w-0 flex-1 ${dimClass}`}>
        <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }} />
          <span className="truncate">{title}</span>
        </div>
        <div className="text-xs text-gray-500 truncate">{subtitle}</div>
      </div>

      {hint && (
        <span
          className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg max-w-[85%] opacity-0 translate-x-3 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-x-0 ${
            clickable ? "text-white" : "bg-gray-800/95 text-white"
          }`}
          style={clickable ? { background: meta.color } : {}}
        >
          {HintIcon && <HintIcon className="w-3.5 h-3.5 flex-shrink-0" />}
          <span className="truncate">{hint}</span>
        </span>
      )}
    </button>
  );
};

export default function ImportantDatesRail() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const isAdmin = !!user && (user.is_superuser || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    importantDatesService
      .getImportantDates()
      .then((data) => {
        if (!cancelled) {
          setItems(data?.items || []);
          setEnabled(data?.enabled !== false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAction = (item) => {
    if (item.kind === "birthday" || item.kind === "anniversary") {
      navigate(
        `/recognition?recipientId=${item.user.id}&typeId=${item.recognition_type_id}&occasion=${item.kind}`
      );
      return;
    }
    if (!item.use_nominations) {
      navigate(
        `/recognition?typeId=${item.recognition_type_id}&occasion=event&eventName=${encodeURIComponent(item.event.name)}`
      );
      return;
    }
    if (item.campaign_id && item.campaign_active) {
      navigate(`/nominations?campaignId=${item.campaign_id}`);
    } else if (item.campaign_id && isAdmin) {
      navigate(`/admin/campaigns/${item.campaign_id}`);
    } else if (isAdmin) {
      navigate(
        `/admin?tab=nominations&createCampaign=1&eventId=${item.event.id}&eventName=${encodeURIComponent(item.event.name)}`
      );
    }
  };

  if (error || !enabled) return null;

  return (
    <div className="hidden lg:block w-96 flex-shrink-0 sticky top-8 mt-[62px] bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <CalendarHeart className="w-4 h-4 text-purple-600" />
        <h3 className="text-sm font-semibold text-gray-900">Important Dates</h3>
      </div>
      <div className="p-2 max-h-[28rem] overflow-y-auto">
        {loading ? (
          <div className="py-6 flex justify-center">
            <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6 px-3">
            No upcoming birthdays, anniversaries, or events.
          </p>
        ) : (
          items.map((item, idx) => (
            <ImportantDateRow
              key={`${item.kind}-${item.user?.id || item.event?.id || idx}-${item.date}`}
              item={item}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              onAction={handleAction}
            />
          ))
        )}
      </div>
    </div>
  );
}
